import {
    isPotentialAggregateConstraint,
    isPriorQueueConstraint,
    ScenarioConstraint
} from "in_queue/types/scenarioResultType";

import { ViewType } from "../ribbon/ViewTypesMenu";

// Scope selected by the view selector (Only my project / Related / All results).
export interface ConstraintScope {
    viewType: ViewType;
    currentProjectId: string;
    relatedProjectIds: Set<string>;
}

// Does a current-cycle (CQ) row pass the view-selector scope filter?
// (relatedProjectIds includes the current project, so "related" covers my + related.)
//
// In the Related view another project's potential aggregate rows are hidden: that project
// overloads the line but pays nothing for it, so the row says nothing about shared cost. The
// current project's own rows always show, potential or not. Only PJM writes that criterion, so
// this is a no-op for every other region.
const passesCqScope = (
    row: Pick<ScenarioConstraint, "projectId" | "metadata">,
    { viewType, currentProjectId, relatedProjectIds }: ConstraintScope
): boolean => {
    if (viewType === "all") {
        return true;
    }
    if (row.projectId === currentProjectId) {
        return true;
    }
    if (viewType === "related") {
        return (
            relatedProjectIds.has(row.projectId) &&
            !isPotentialAggregateConstraint(row)
        );
    }
    return false;
};

// A PQ contributor is matched to a CQ constraint only when it overloads the *same monitored
// facility under the same contingency*. Matching on facility alone surfaces PQ rows for
// unrelated contingencies, which aren't meaningful to the user.
const facilityContingencyKey = (
    c: Pick<
        ScenarioConstraint,
        "monitoredFacilityName" | "worstContingencyName"
    >
): string =>
    JSON.stringify([c.monitoredFacilityName, c.worstContingencyName ?? ""]);

// The (facility, contingency) keys of the CQ constraints *currently in view* -- i.e. the same
// rows that pass the view-selector scope. A PQ row is surfaced (in the narrowed views) only if
// it overloads one of these, so PQ tracks whatever CQ you're looking at:
//   - Only my project -> the current project's constraints
//   - Related         -> the current + related projects' constraints
// (In the All-results view PQ rows are not gated by this set -- every PQ row shows.)
export const computeInScopePqKeys = (
    constraints: ScenarioConstraint[],
    scope: ConstraintScope
): Set<string> => {
    const keys = new Set<string>();
    for (const c of constraints) {
        if (isPriorQueueConstraint(c)) {
            continue;
        }
        if (passesCqScope(c, scope)) {
            keys.add(facilityContingencyKey(c));
        }
    }
    return keys;
};

// ag-Grid runs the external filter only when this returns true. It must stay active when there
// are PQ rows to gate -- even in the "All results" view, which otherwise has no scope filter.
export const isExternalConstraintFilterPresent = (
    viewType: ViewType,
    hasPriorQueue: boolean
): boolean => viewType !== "all" || hasPriorQueue;

// Per-row external filter decision.
export const doesConstraintPassFilter = (
    row: ScenarioConstraint,
    opts: ConstraintScope & {
        includePqContributors: boolean;
        inScopePqKeys: Set<string>;
    }
): boolean => {
    if (isPriorQueueConstraint(row)) {
        // PQ rows are hidden unless the toggle is on.
        if (!opts.includePqContributors) {
            return false;
        }
        // All results: show every PQ row. Narrowed views: only PQ contributors that overload one
        // of the in-view CQ constraints, matched on facility + contingency.
        return (
            opts.viewType === "all" ||
            opts.inScopePqKeys.has(facilityContingencyKey(row))
        );
    }
    // Current-cycle rows: the view-selector scope filter.
    return passesCqScope(row, opts);
};
